import React, { useState } from "react";
import "../../css/Common.css";
import "./css/PhoneNumberScamCheck.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faExclamationTriangle,
  faShieldAlt,
  faTimes,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faSearch,
  faDatabase,
} from "@fortawesome/free-solid-svg-icons";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import HelpGuideButton from "../../HelpGuideButton";
import EmptyStatePanel from "../common/EmptyStatePanel";
import {
  getRiskLevelIcon,
  getThreatBannerClass,
  getThreatTitle,
  getThreatSubtitle,
  isHighRisk,
  isMediumRisk,
} from "./textAnalysisUtils";
import ThreatBannerShareButton from "../common/ThreatBannerShareButton";
import DisclosuresModal from "../common/DisclosuresModal";
import { useTranslation } from "../../../../i18n/LanguageContext";

const PhoneNumberScamCheckDesktop = ({
  phoneNumber,
  context,
  setContext,
  searchResults,
  isLoading,
  error,
  hasUserInput,
  phoneValidation,
  reportId,
  handlePhoneChange,
  handleSearch,
  handleClearSearch,
  handleKeyPress,
  phoneInputKey,
}) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);

  const { t } = useTranslation();

  const renderInputForm = () => (
    <>
      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        className="analysis-form"
      >
        <div className="common-form-row">
          <div className="common-form-field">
            <label htmlFor="phone-number">
              {t("phoneCheck.form.phoneLabel")}
            </label>
            <div className="phone-input-container">
              <InternationalPhoneInput
                key={phoneInputKey}
                id="phone-number"
                value={phoneNumber}
                onChange={handlePhoneChange}
                onKeyPress={handleKeyPress}
                placeholder={t("phoneCheck.form.phonePlaceholder")}
                className="phone-input compact"
              />
              {!phoneValidation.isValid &&
                phoneValidation.isComplete &&
                phoneNumber &&
                phoneNumber.length > 2 && (
                  <div className="validation-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {t("phoneCheck.form.invalidPhone")}
                  </div>
                )}
            </div>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: "1rem" }}>
          <label htmlFor="phone-context-input">
            <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
            {t("phoneCheck.form.contextLabel")}
          </label>
          <textarea
            id="phone-context-input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("phoneCheck.form.contextPlaceholder")}
            className="additional-context-textarea"
            rows={3}
          />
        </div>

        <div className="btn-group">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              isLoading ||
              !hasUserInput ||
              !phoneValidation.isValid ||
              !phoneValidation.isComplete
            }
          >
            <FontAwesomeIcon icon={faPhone} />
            {isLoading
              ? t("phoneCheck.form.analyzing")
              : t("phoneCheck.form.checkBtn")}
          </button>

          {hasUserInput && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClearSearch}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faTimes} />
              {t("phoneCheck.form.clearBtn")}
            </button>
          )}
        </div>
      </form>

      {/* Info Section */}
      <div className="info-section">
        <div className="tips-section">
          <h4>{t("phoneCheck.tips.heading")}</h4>
          <ul>
            <li>{t("phoneCheck.tips.tip1")}</li>
            <li>{t("phoneCheck.tips.tip2")}</li>
            <li>{t("phoneCheck.tips.tip3")}</li>
            <li className="verify-tip-container">
              {t("phoneCheck.tips.tip4")} &nbsp;&nbsp;
              <HelpGuideButton guideId={13} />
            </li>
            <li>{t("phoneCheck.tips.tip5")}</li>
            <li>{t("phoneCheck.tips.tip6")}</li>
            <li>{t("phoneCheck.tips.tip7")}</li>
          </ul>
        </div>
        <h4>{t("phoneCheck.tips.howTitle")}</h4>
        <p>{t("phoneCheck.tips.howDesc")}</p>
      </div>
    </>
  );

  const renderResultsContent = () => {
    const scamDetected =
      searchResults &&
      (isHighRisk(searchResults.riskLevel) ||
        isMediumRisk(searchResults.riskLevel));

    if (!searchResults) {
      return (
        <EmptyStatePanel
          icon={faPhone}
          title={t("phoneCheck.results.readyTitle")}
          description={t("phoneCheck.results.readyDesc")}
          className="no-results"
        />
      );
    }

    return (
      <>
        {/* Threat Assessment Banner */}
        <div
          className={`threat-banner pnsc-threat-banner ${getThreatBannerClass(searchResults.riskLevel)}`}
        >
          <div className="threat-icon">
            <FontAwesomeIcon icon={getRiskLevelIcon(searchResults.riskLevel)} />
          </div>
          <h2 className="threat-title">
            {getThreatTitle(searchResults.riskLevel)}
          </h2>
          <ThreatBannerShareButton
            scamDetected={scamDetected}
            reportId={reportId}
            threatType="Phone Number Scam"
            threatTitle={getThreatTitle(searchResults.riskLevel)}
            threatDetail={getThreatSubtitle(searchResults.riskLevel)}
          />
        </div>

        {/* What Should You Do? */}
        {searchResults.recommendations &&
          searchResults.recommendations.length > 0 && (
            <div className="suggestions-section">
              <h4 className="subsection-title">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="fa-icon-margin-right-8"
                />
                {t("phoneCheck.results.whatToDo")}
              </h4>
              <ul className="recommendations-list">
                {searchResults.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

        {/* Explanation */}
        {searchResults.summary && (
          <div className="explanation-section">
            <h4
              className="subsection-title collapsible-header"
              onClick={() => setExplanationCollapsed(!explanationCollapsed)}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                className="fa-icon-margin-right-8"
              />
              {t("phoneCheck.results.explanation")}
              <FontAwesomeIcon
                icon={explanationCollapsed ? faChevronDown : faChevronUp}
                style={{ marginLeft: "auto", fontSize: "0.85rem" }}
              />
            </h4>
            {!explanationCollapsed && (
              <div
                className="explanation-text"
                dangerouslySetInnerHTML={{
                  __html: searchResults.summary,
                }}
              />
            )}
          </div>
        )}

        {/* Warning */}
        <div className="warning-box pnsc-warning-box">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <div className="pnsc-warning-text">
            <strong>{t("phoneCheck.results.warningImportant")}</strong>
            <span>{t("phoneCheck.results.warningText")}</span>
          </div>
        </div>

        {/* Database Results - Collapsed by default */}
        <div className="explanation-section">
          <h4
            className="subsection-title collapsible-header"
            onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon
              icon={faDatabase}
              className="fa-icon-margin-right-8"
            />
            {t("phoneCheck.results.dbResults")}
            {searchResults.sources &&
              searchResults.sources.length > 0 &&
              ` (${searchResults.sources.length})`}
            <FontAwesomeIcon
              icon={isDetailsExpanded ? faChevronUp : faChevronDown}
              style={{ marginLeft: "auto", fontSize: "0.85rem" }}
            />
          </h4>
          {isDetailsExpanded && (
            <div style={{ marginTop: "var(--spacing-md)" }}>
              {searchResults.sources && searchResults.sources.length > 0 ? (
                searchResults.sources.map((source, index) => (
                  <div key={index} className="source-item">
                    <div className="source-header">
                      <FontAwesomeIcon
                        icon={faShieldAlt}
                        className="source-icon"
                      />
                      <h5>{source.name}</h5>
                    </div>
                    <div className="compact-analysis">
                      {source.category && (
                        <div className="analysis-line">
                          <strong>{t("phoneCheck.results.dbCategory")}</strong>{" "}
                          {source.category}
                        </div>
                      )}
                      {source.description && (
                        <div className="analysis-line">
                          <strong>{t("phoneCheck.results.dbSummary")}</strong>{" "}
                          {source.description}
                        </div>
                      )}
                      {source.details &&
                        Object.entries(source.details).map(([key, value]) => (
                          <div key={key} className="analysis-line">
                            <strong>{key}:</strong> {value}
                          </div>
                        ))}
                      {source.reports !== null &&
                        source.reports !== undefined &&
                        source.reports > 0 && (
                          <div className="analysis-line">
                            <strong>{t("phoneCheck.results.dbReports")}</strong>{" "}
                            {source.reports}
                          </div>
                        )}
                      {source.lastReported && (
                        <div className="analysis-line">
                          <strong>
                            {t("phoneCheck.results.dbLastReported")}
                          </strong>{" "}
                          {source.lastReported}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ color: "var(--text-secondary)" }}>
                  {t("phoneCheck.results.dbNoSources")}
                </p>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="common-header">
        <h1 className="main-title">{t("phoneCheck.page.title")}</h1>
        <div className="subtitle-with-help">
          <p className="common-subtitle">{t("phoneCheck.page.subtitle")}</p>
          <HelpGuideButton guideId={5} />
        </div>
      </div>

      {/* Two Panel Layout */}
      <div className="analysis-layout two-panel phone-scam-check-layout">
        {/* Left Panel - Input Form */}
        <div className="left-panel phone-scam-check-left">
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faPhone} />
              {t("phoneCheck.desktop.inputPanelTitle")}
            </h3>
          </div>
          <div className="panel-content scrollable-panel">
            {renderInputForm()}
          </div>
          ``
        </div>

        {/* Right Panel - Analysis Results */}
        <div className="right-panel phone-scam-check-right">
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faShieldAlt} />
              {t("phoneCheck.desktop.resultsPanelTitle")}
            </h3>
          </div>
          <div className="panel-content scrollable-panel">
            {renderResultsContent()}
          </div>
        </div>
      </div>

      <DisclosuresModal disclaimerText={t("phoneCheck.disclaimer")} />
    </>
  );
};

export default PhoneNumberScamCheckDesktop;
